/**
 * Utility functions for Matchplay v2
 */

import { courses } from '../../data/courses';

/**
 * Format date strings as local dates to avoid timezone issues
 * Handles both date-only strings (YYYY-MM-DD) and full datetime strings
 */
export function formatLocalDate(dateString: string, options?: Intl.DateTimeFormatOptions): string {
  try {
    if (dateString.includes('T')) {
      // For full datetime strings (like createdAt), use UTC noon to avoid timezone shifts
      const date = new Date(`${dateString.split('T')[0]}T12:00:00Z`);
      return date.toLocaleDateString('en-US', options || {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else {
      // For date-only strings (like startDate), create local date
      const [year, month, day] = dateString.split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      
      return localDate.toLocaleDateString('en-US', options || {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return dateString; // Return original string on error
  }
}

// Helper for match dates, which are typically full datetime strings from Supabase
export function formatMatchDate(dateString: string): string {
    return formatLocalDate(dateString, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Helper for tournament dates, which are typically date-only strings
export function formatTournamentDate(dateString: string): string {
    return formatLocalDate(dateString, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Get par values for a specific course
 * Returns default par values if course is not found
 */
export function getParValues(courseId?: string): number[] {
  if (!courseId) {
    // Return default par values if no course specified
    return [4, 4, 3, 4, 5, 4, 3, 4, 4, 4, 4, 3, 5, 4, 4, 3, 4, 5];
  }

  const course = courses.find(c => c.id === courseId);
  if (!course) {
    console.warn(`Course not found: ${courseId}, using default par values`);
    // Return default par values if course not found
    return [4, 4, 3, 4, 5, 4, 3, 4, 4, 4, 4, 3, 5, 4, 4, 3, 4, 5];
  }

  return course.parValues;
}

/**
 * Get course name for display
 */
export function getCourseName(courseId?: string): string {
  if (!courseId) return 'Unknown Course';
  
  const course = courses.find(c => c.id === courseId);
  return course?.name || 'Unknown Course';
} 